import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Fade, Tabs, Tab } from "@mui/material";
import { TabContext, TabPanel } from "@mui/lab";
import FormBlockBrowser from "./FormBlockBrowser";
import FormFieldPanel from "./FormFieldPanel";
import DefualtFormFieldPanel from "./DefualtFormFieldPanel";

const MIN_WIDTH = 270;
const MAX_WIDTH = 600;
const DEFAULT_WIDTH = 330;

const PDFEditorLeftPanel = ({
  currentProcessingFile,
  setCurrentProcessingFile,
  isOpen,
  onNotify,
  loading,
  fields,
  getFormFieldsList,
  fieldNavigationTarget,
  onFieldNavigationComplete,
}) => {
  const [tabValue, setTabValue] = useState("1");
  const [width, setWidth] = useState(DEFAULT_WIDTH);
  const [crossTabNavigationTarget, setCrossTabNavigationTarget] =
    useState(null);
  const isResizing = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(0);

  const activeFieldNavigationTarget =
    crossTabNavigationTarget || fieldNavigationTarget;

  const handleChange = (event, newValue) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    if (activeFieldNavigationTarget?.tabValue) {
      setTabValue(activeFieldNavigationTarget.tabValue);
    }
  }, [activeFieldNavigationTarget]);

  const handleNavigateToField = useCallback((target) => {
    setCrossTabNavigationTarget({
      ...target,
      requestId: Date.now(),
    });
  }, []);

  const handleFieldNavigationComplete = useCallback(
    (requestId) => {
      if (crossTabNavigationTarget?.requestId === requestId) {
        setCrossTabNavigationTarget(null);
        return;
      }

      onFieldNavigationComplete?.(requestId);
    },
    [crossTabNavigationTarget, onFieldNavigationComplete],
  );

  const onMouseDown = useCallback(
    (e) => {
      isResizing.current = true;
      startX.current = e.clientX;
      startWidth.current = width;

      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";

      const onMouseMove = (e) => {
        if (!isResizing.current) return;
        const delta = e.clientX - startX.current;
        const newWidth = Math.min(
          MAX_WIDTH,
          Math.max(MIN_WIDTH, startWidth.current + delta),
        );
        setWidth(newWidth);
      };

      const onMouseUp = () => {
        isResizing.current = false;
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
      };

      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    },
    [width],
  );

  return (
    <Fade in>
      <Box
        sx={{
          display: "flex",
          position: "relative",
          alignSelf: "stretch",
          flexShrink: 0,
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: isOpen ? width : 0,
            minWidth: isOpen ? MIN_WIDTH : 0,
            maxWidth: isOpen ? MAX_WIDTH : 0,
            bgcolor: "background.paper",
            alignSelf: "stretch",
            flexShrink: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          <TabContext value={tabValue}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                flex: "1 1 0",
                minHeight: 0,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  bgcolor: "background.paper",
                  flexShrink: 0,
                }}
              >
                <Tabs
                  value={tabValue}
                  onChange={handleChange}
                  centered
                  variant="fullWidth"
                >
                  <Tab
                    label="Form Sections"
                    value="1"
                    sx={{ margin: "0px !important" }}
                  />
                  <Tab
                    label="Form Fields"
                    value="2"
                    sx={{ margin: "0px !important" }}
                  />
                  <Tab
                    label="Default Fields"
                    value="3"
                    sx={{ margin: "0px !important" }}
                  />
                </Tabs>
              </Box>
              <TabPanel
                value="1"
                sx={{
                  padding: "0px !important",
                  flex: 1,
                  minHeight: 0,
                  overflow: "hidden",
                }}
              >
                <FormBlockBrowser
                  currentProcessingFile={currentProcessingFile}
                  setCurrentProcessingFile={setCurrentProcessingFile}
                  onNotify={onNotify}
                  loading={loading}
                  fields={fields}
                />
              </TabPanel>
              <TabPanel
                value="2"
                sx={{
                  padding: "0px !important",
                  flex: 1,
                  minHeight: 0,
                  overflow: "hidden",
                }}
              >
                <FormFieldPanel
                  currentProcessingFile={currentProcessingFile}
                  setCurrentProcessingFile={setCurrentProcessingFile}
                  onNotify={onNotify}
                  loading={loading}
                  getFormFieldsList={getFormFieldsList}
                  fieldNavigationTarget={activeFieldNavigationTarget}
                  onFieldNavigationComplete={handleFieldNavigationComplete}
                  onNavigateToField={handleNavigateToField}
                />
              </TabPanel>
              <TabPanel
                value="3"
                sx={{
                  padding: "0px !important",
                  flex: 1,
                  minHeight: 0,
                  overflow: "hidden",
                }}
              >
                <DefualtFormFieldPanel
                  currentProcessingFile={currentProcessingFile}
                  setCurrentProcessingFile={setCurrentProcessingFile}
                  onNotify={onNotify}
                  loading={loading}
                  getFormFieldsList={getFormFieldsList}
                  fieldNavigationTarget={activeFieldNavigationTarget}
                  onFieldNavigationComplete={handleFieldNavigationComplete}
                  onNavigateToField={handleNavigateToField}
                />
              </TabPanel>
            </Box>
          </TabContext>
        </Box>
        <Box
          onMouseDown={onMouseDown}
          sx={{
            width: 5,
            cursor: "col-resize",
            bgcolor: "transparent",
            borderRight: "1px solid #E5E7EB",
            flexShrink: 0,
            transition: "background-color 0.15s",
            "&:hover": {
              bgcolor: "primary.main",
              opacity: 0.4,
            },
            "&:active": {
              bgcolor: "primary.main",
              opacity: 0.6,
            },
          }}
        />
      </Box>
    </Fade>
  );
};

export default PDFEditorLeftPanel;
